import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Observable, EMPTY, of, throwError } from 'rxjs';
import { mergeMap, catchError } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { PjAccountContextService } from '../shared/services/account/pj-account-context.service';
import { PjPendingApprovalModalComponent } from '../shared/pj-pending-approval-modal/pj-pending-approval-modal.component';

/**
 * PJ Banking HTTP Interceptor.
 *
 * 1) Injects `x-pj-account-id` header on banking requests when PJ context is active
 * 2) Intercepts 202 responses with `status: 'PENDING_APPROVAL'` from PJ operations
 *    and opens a friendly modal instead of letting it fall through as an error
 */
@Injectable()
export class PjAccountHttpInterceptor implements HttpInterceptor {

    private readonly BANKING_URL_PATTERNS = [
        '/api/v1/bank-accounts/',
        '/api/v1/pix/',
        '/api/v1/bills/',
        '/api/v1/pj-banking/'
    ];

    constructor(
        private readonly pjContext: PjAccountContextService,
        private readonly dialog: MatDialog
    ) {}

    public intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        const pjAccountId = this.pjContext.getActivePjAccountId();
        let request = req;

        // Inject PJ header on banking requests
        if (pjAccountId && this.isBankingRequest(req.url)) {
            request = req.clone({
                setHeaders: { 'x-pj-account-id': pjAccountId }
            });
        }

        // Only intercept 202 responses when PJ context is active
        if (!pjAccountId) {
            return next.handle(request);
        }

        return next.handle(request).pipe(
            mergeMap((event: HttpEvent<any>) => {
                if (event instanceof HttpResponse && this.isPjPending(event.body)) {
                    this.openPendingModal(event.body);
                    return EMPTY;
                }
                return of(event);
            }),
            catchError((error: HttpErrorResponse) => {
                if (error.status === 202 && this.isPjPending(error.error)) {
                    this.openPendingModal(error.error);
                    return EMPTY;
                }
                return throwError(error);
            })
        );
    }

    private isPjPending(body: any): boolean {
        return body?.status === 'PENDING_APPROVAL' && !!body?.requestId;
    }

    private openPendingModal(data: any): void {
        this.dialog.open(PjPendingApprovalModalComponent, {
            data,
            width: '90%',
            maxWidth: '440px',
            disableClose: false
        });
    }

    private isBankingRequest(url: string): boolean {
        return this.BANKING_URL_PATTERNS.some(pattern => url.includes(pattern));
    }
}
